import React, { useState } from 'react'
import { Select as MuiSelect, MenuItem, FormControl, TextField } from '@mui/material'
import { AdapterMoment } from '@mui/x-date-pickers/AdapterMoment'
import { DesktopDatePicker } from '@mui/x-date-pickers'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import moment, { Moment } from 'moment-timezone'

interface CurrentFilters {
  startDate?: string
  endDate?: string
  valueOperator?: string
  valueAmount?: string
}

interface HeatEnergyReadingsFiltersProps {
  currentFilters: CurrentFilters
}

const VALUE_OPERATORS = [
  { value: 'greater_than', label: 'Greater than' },
  { value: 'less_than', label: 'Less than' },
  { value: 'equals', label: 'Equal to' }
]

const datePickerSx = {
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#dee2e6'
    }
  }
}

const HeatEnergyReadingsFilters: React.FC<HeatEnergyReadingsFiltersProps> = ({ currentFilters }) => {
  const [startDate, setStartDate] = useState<Moment | null>(
    currentFilters.startDate ? moment(currentFilters.startDate) : null
  )
  const [endDate, setEndDate] = useState<Moment | null>(
    currentFilters.endDate ? moment(currentFilters.endDate) : null
  )
  const [valueOperator, setValueOperator] = useState<string>(currentFilters.valueOperator || 'equals')
  const [valueAmount, setValueAmount] = useState<string>(currentFilters.valueAmount || '')

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()

    const params = new URLSearchParams()

    // A half-typed date is a Moment that formats to "Invalid date" — drop it rather
    // than sending text the server cannot parse.
    if (startDate?.isValid()) params.append('start_date', startDate.format('YYYY-MM-DD'))
    if (endDate?.isValid()) params.append('end_date', endDate.format('YYYY-MM-DD'))
    if (valueOperator && valueAmount) {
      params.append('value_operator', valueOperator)
      params.append('value_amount', valueAmount)
    }

    window.location.href = `/admin/heat_energy_readings?${params.toString()}`
  }

  const handleClear = () => {
    window.location.href = '/admin/heat_energy_readings'
  }

  return (
    <div className="card mb-3">
      <div className="card-body">
        <form onSubmit={handleSubmit}>
          <div className="row g-3">
            <div className="col-md-3">
              <label className="form-label">Date From</label>
              <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
                <DesktopDatePicker
                  className="w-100"
                  value={startDate}
                  onChange={(date) => setStartDate(date)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  sx={datePickerSx}
                />
              </LocalizationProvider>
            </div>

            <div className="col-md-3">
              <label className="form-label">Date To</label>
              <LocalizationProvider dateAdapter={AdapterMoment} dateLibInstance={moment}>
                <DesktopDatePicker
                  className="w-100"
                  value={endDate}
                  onChange={(date) => setEndDate(date)}
                  slotProps={{ textField: { size: 'small', fullWidth: true } }}
                  sx={datePickerSx}
                />
              </LocalizationProvider>
            </div>

            <div className="col-md-6">
              <label className="form-label">Heat Energy In Pan (kW)</label>
              <div className="d-flex gap-2">
                <FormControl size="small" style={{ minWidth: '140px' }}>
                  <MuiSelect
                    value={valueOperator}
                    onChange={(e) => setValueOperator(e.target.value)}
                    IconComponent={() => (
                      <i className="fa fa-chevron-down" style={{ color: '#757575', marginRight: '12px', fontWeight: 300, fontSize: '12px' }}/>
                    )}
                    sx={{
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#dee2e6'
                      },
                      '& .MuiSelect-icon': {
                        top: 'calc(50% - 5px)',
                        right: '8px'
                      }
                    }}
                  >
                    {VALUE_OPERATORS.map(op => (
                      <MenuItem key={op.value} value={op.value}>
                        {op.label}
                      </MenuItem>
                    ))}
                  </MuiSelect>
                </FormControl>
                <TextField
                  size="small"
                  type="number"
                  placeholder="Value"
                  value={valueAmount}
                  onChange={(e) => setValueAmount(e.target.value)}
                  inputProps={{ step: '0.1' }}
                  sx={{
                    width: '120px',
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': {
                        borderColor: '#dee2e6'
                      }
                    }
                  }}
                />
              </div>
            </div>
          </div>

          <div className="d-flex gap-2 mt-3 justify-content-end">
            <button type="submit" className="btn btn-primary">
              Search
            </button>
            <button type="button" className="btn btn-secondary" onClick={handleClear}>
              Clear
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default HeatEnergyReadingsFilters
